import Link from 'next/link';
import {
  SignIn,
  SignUp,
  UserButton,
  UserProfile,
  SignedIn,
  SignedOut
} from '@clerk/nextjs';
import {
  Button,
  Tbody,
  Text,
  Tfoot,
  Tr,
  Box,
  UnorderedList,
  OrderedList,
  ListItem,
  TabPanel,
  AccordionPanel
} from '@chakra-ui/react';
import Image from 'next/image';
import { ExternalLinkIcon } from '@heroicons/react/solid';

import { KeyToggleProvider } from '@context';
import { detectExternalLink, makeDocsLink } from '../utils';
import { Heading } from './heading';
import { PreBlock } from './code-block';
import { CodeBlockTabs } from './code-block-tabs';
import { ReactElement } from 'react';
import { Callout } from './callout';
import { InjectKeys } from './inject-keys';
import { Steps } from './steps';
import { Card } from './card';
import { ComponentPreviewCard } from './component-preview-card';
import { Tables } from './tables';
import { DocsImage } from './image';
import { Table, TableDataCell, TableHead, TableHeadingCell } from './table';
import { TutorialHero } from './tutorial-hero';
import { Tabs } from './tabs';
import { Accordion } from './accordion';

/**
 * A map of custom components that are provided to MDX for rendering. Native elements are overridden here, as well as all new components that can be used in our docs.
 */
export const components = {
  SignIn(props: any) {
    return <SignIn {...props} />;
  },
  SignUp(props: any) {
    return <SignUp {...props} />;
  },
  UserButton(props: any) {
    return <UserButton {...props} />;
  },
  UserProfile(props: any) {
    return <UserProfile {...props} />;
  },
  SignedIn(props: any) {
    return <SignedIn {...props} />;
  },
  SignedOut(props: any) {
    return <SignedOut {...props} />;
  },
  Callout,
  button({ children, ...props }: any) {
    return (
      <Button as="button" onClick={() => eval(props?.onclick)} {...props}>
        {children}
      </Button>
    );
  },
  pre: PreBlock,
  a({ children, href }: any) {
    const isExternalLink = detectExternalLink(href);
    return (
      <Box
        as={Link}
        href={href}
        target={isExternalLink ? '_blank' : '_self'}
        color="var(--secondaryBlue)"
        sx={{
          '& code': {
            color: 'var(--tertiaryBlue)'
          }
        }}
      >
        {children}
        {isExternalLink && (
          <>
            <span className="sr-only">(opens in a new tab)</span>
            <ExternalLinkIcon className="relative bottom-[1.25px] inline w-4" />
          </>
        )}
      </Box>
    );
  },
  h1({ children, ...props }: any) {
    return (
      <Heading {...props} as="h1" size="docsh1" mt={0}>
        {children}
      </Heading>
    );
  },
  h2({ children, ...props }: any) {
    return (
      <Heading {...props} as="h2" size="docsh2">
        {children}
      </Heading>
    );
  },
  h3({ children, ...props }: any) {
    return (
      <Heading {...props} as="h3" size="docsh3">
        {children}
      </Heading>
    );
  },
  h4({ children, ...props }: any) {
    return (
      <Heading {...props} as="h4" size="docsh4">
        {children}
      </Heading>
    );
  },
  h5({ children, ...props }: any) {
    return (
      <Heading {...props} as="h5" size="docsh5">
        {children}
      </Heading>
    );
  },
  h6({ children, ...props }: any) {
    return (
      <Heading {...props} as="h6" size="docsh6">
        {children}
      </Heading>
    );
  },
  table: Table,
  tbody: Tbody,
  thead: TableHead,
  tfoot: Tfoot,
  th: TableHeadingCell,
  tr: Tr,
  td: TableDataCell,
  p({ children, ...props }: any) {
    return (
      <Text as="p" my={4} lineHeight={1.8} {...props}>
        {children}
      </Text>
    );
  },
  div({ children, ...props }: any) {
    return (
      <Box as="div" {...props}>
        {children}
      </Box>
    );
  },
  ul: UnorderedList,
  ol: (props: any) => <OrderedList my={4} {...props} />,
  li: (props: any) => <ListItem my={2} {...props} />,
  blockquote({ children }: any) {
    return <Callout>{children}</Callout>;
  },
  img(props: any) {
    return <Image alt={props.alt} src={props.src} width={1000} height={500} />;
  },
  CodeBlockTabs,
  Cards: ({ title, link: url, description, cta: ctaText }: any) => {
    return (
      <Card
        title={title}
        url={url}
        description={description}
        ctaText={ctaText}
      />
    );
  },
  AuthProvidersCards: ({
    title,
    link: url,
    description,
    cta: ctaText,
    icon: logo
  }: any) => {
    return (
      <Card
        title={title}
        url={url}
        description={description}
        ctaText={ctaText}
        logo={makeDocsLink(logo)}
      />
    );
  },
  FrameworkCards: ({
    title,
    link: url,
    description,
    cta: ctaText,
    icon: logo
  }: any) => {
    return (
      <Card
        title={title}
        url={url}
        description={description}
        ctaText={ctaText}
        logo={makeDocsLink(logo)}
      />
    );
  },
  ComponentPreviewCards: ({
    title,
    link: url,
    description,
    cta: ctaText,
    image: logo
  }: any) => {
    return (
      <ComponentPreviewCard
        title={title}
        url={url}
        description={description}
        ctaText={ctaText}
        logo={makeDocsLink(logo)}
        logoHeight={300}
        logoWidth={300}
      />
    );
  },
  Tab: ({ children }: { children: ReactElement }) => (
    <TabPanel pt={0}>{children}</TabPanel>
  ),
  Tabs,
  Steps,
  Tables,
  InjectKeys: (props: { children: ReactElement }) => (
    <KeyToggleProvider>
      <InjectKeys {...props} />
    </KeyToggleProvider>
  ),
  Images: DocsImage,
  TutorialHero,
  Accordion,
  AccordionPanel,

  // TODO: implement, or find alternatives
  Hero: () => null,
  Subheader: () => null
};
